import {
  CheckCircleIcon,
  PencilSimpleIcon,
  PlusIcon,
  XCircleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { css, html } from 'react-strict-dom';
import { Api } from 'src/api';
import useApiCallable from 'src/api/use-api-callable';
import {
  Dropdown,
  DropdownMenuItem,
  IconButton,
  Stack,
  Text,
} from 'src/components';
import { Color, Style } from 'src/constants';
import { color } from 'src/constants/color.css';
import { Model } from 'src/models';
import { capitalize } from 'src/util';

import AvatarGroup from './AvatarGroup';
import SkillAssigneeDropdown from './SkillAssigneeDropdown';

type TicketSkill = Api.Ticket.V_Detail['skills'][number];

const SKILL_TYPES = [
  { value: 'design', label: 'Design' },
  { value: 'backend', label: 'BE' },
  { value: 'frontend', label: 'FE' },
  { value: 'qa', label: 'QA' },
];

const SKILL_ORDER = SKILL_TYPES.map((s) => s.value);

function skillRank(type: string) {
  const index = SKILL_ORDER.indexOf(type);
  return index === -1 ? SKILL_ORDER.length : index;
}

type Props = {
  ticketId: string;
  skills: TicketSkill[];
  agencyUsers: ReadonlyArray<Api.User.BaseView>;
  invalidate: () => void;
};

const SKILL_STATUSES = [
  { value: 'awaiting_review', label: 'Awaiting Review' },
  { value: 'done', label: 'Done' },
] as const;

const STATUS_COLORS: Record<
  string,
  { text: keyof typeof Color; bg: string; border: string }
> = {
  done: { text: 'blue', bg: color.blueBg, border: color.blue },
  awaiting_review: {
    text: 'accent',
    bg: color.accentBg,
    border: color.accent,
  },
};

function StatusTag({ status }: { status: string }) {
  const label = SKILL_STATUSES.find((s) => s.value === status)?.label ?? status;
  const colors = STATUS_COLORS[status] ?? {
    text: 'neutral80' as const,
    bg: color.neutral10,
    border: color.neutral40,
  };

  return (
    <html.div style={[styles.tag, styles.tagColors(colors.bg, colors.border)]}>
      <Text typeface="body.medium" color={colors.text}>
        {label}
      </Text>
    </html.div>
  );
}

const APPROVAL_LABELS: Readonly<Record<Model.TicketSkill.Approval, string>> = {
  approved: 'Approved',
  rejected: 'Rejected',
};

/**
 * Sits alongside the skill name: approve/reject while nobody has decided,
 * otherwise the decision and a way to undo it.
 */
function SkillApproval({
  skill,
  onChange,
}: {
  skill: TicketSkill;
  onChange: (approval: Model.TicketSkill.Approval | null) => void;
}) {
  const approval = Model.TicketSkill.getApproval(skill);

  // Null is nobody having decided yet; rejected is a decision, like approved.
  if (approval === null) {
    return (
      <html.div style={[Style.row, Style.alignCenter]}>
        <IconButton
          aria-label={`Approve ${skill.type}`}
          tooltip="Approve"
          onClick={() => onChange('approved')}
        >
          <CheckCircleIcon size={20} color={Color.accent} />
        </IconButton>
        <IconButton
          aria-label={`Reject ${skill.type}`}
          tooltip="Reject"
          onClick={() => onChange('rejected')}
        >
          <XCircleIcon size={20} color={Color.accent} />
        </IconButton>
      </html.div>
    );
  }

  return (
    <html.div style={[Style.row, Style.alignCenter]}>
      <Text typeface="body.small.regular" color="neutral60">
        • {APPROVAL_LABELS[approval]}
      </Text>
      <IconButton
        aria-label={`Cancel ${skill.type} ${approval} status`}
        tooltip="Cancel"
        onClick={() => onChange(null)}
      >
        <XCircleIcon size={20} color={Color.neutral60} />
      </IconButton>
    </html.div>
  );
}

export default function SkillsCard({
  ticketId,
  skills,
  agencyUsers,
  invalidate,
}: Props) {
  const callApi = useApiCallable();

  const skillsByType = new Map(skills.map((s) => [s.type, s]));

  async function handleToggleSkill(type: string) {
    const existing = skillsByType.get(type);
    if (existing) {
      await callApi(`/ticket-skills/${existing.id}`, { method: 'DELETE' });
    } else {
      await callApi(`/tickets/${ticketId}/skills`, {
        method: 'POST',
        jsonBody: { type },
      });
    }
    invalidate();
  }

  return (
    <Stack gap={1} style={styles.card}>
      <Stack style={[Style.row, Style.alignCenter, Style.spaceBetween]}>
        <Text typeface="body.regular" color="neutral80">
          Skills
        </Text>
        <Dropdown
          trigger={
            <IconButton aria-label="Add skill">
              <PlusIcon size={20} color={Color.neutral60} />
            </IconButton>
          }
          anchorSide="right"
        >
          {SKILL_TYPES.map(({ value, label }) => (
            <DropdownMenuItem
              key={value}
              showRadio={false}
              selected={skillsByType.has(value)}
              onClick={() => handleToggleSkill(value)}
            >
              {label}
            </DropdownMenuItem>
          ))}
        </Dropdown>
      </Stack>

      <html.hr style={styles.divider} />

      {[...skills]
        .sort((a, b) => skillRank(a.type) - skillRank(b.type))
        .map((skill) => (
          <SkillRow
            key={skill.id}
            skill={skill}
            agencyUsers={agencyUsers}
            invalidate={invalidate}
          />
        ))}
    </Stack>
  );
}

function SkillRow({
  skill,
  agencyUsers,
  invalidate,
}: {
  skill: TicketSkill;
  agencyUsers: ReadonlyArray<Api.User.BaseView>;
  invalidate: () => void;
}) {
  const callApi = useApiCallable();
  const { assignee } = skill;

  async function patchSkill(update: Partial<Api.TicketSkill.W_Update>) {
    await callApi(`/ticket-skills/${skill.id}`, {
      method: 'PATCH',
      jsonBody: update,
    });
    invalidate();
  }

  return (
    <Stack gap={1}>
      <Stack style={[Style.row, Style.alignCenter]} gap={0.25}>
        <Text typeface="body.medium" color="neutral80">
          {capitalize(skill.type)}
        </Text>
        {Model.TicketSkill.requiresApproval(skill) && (
          <SkillApproval
            skill={skill}
            onChange={(approval) =>
              patchSkill({
                approved: Model.TicketSkill.getApprovedValue(approval),
              })
            }
          />
        )}
      </Stack>
      <html.div
        style={[Style.row, Style.alignCenter, Style.wrap, styles.skillContent]}
      >
        {(Model.TicketSkill.isAssignable(skill) || assignee) && (
          <Dropdown
            trigger={
              assignee ? (
                <html.div style={[Style.row, Style.alignCenter, styles.pill]}>
                  <AvatarGroup users={[assignee]} max={1} size={32} />
                  <IconButton aria-label={`Edit ${skill.type} assignee`}>
                    <PencilSimpleIcon size={20} color={Color.neutral60} />
                  </IconButton>
                </html.div>
              ) : (
                <html.div
                  style={[
                    Style.row,
                    Style.alignCenter,
                    styles.pill,
                    styles.emptyPill,
                  ]}
                >
                  <IconButton aria-label={`Add ${skill.type} assignee`}>
                    <PlusIcon size={20} color={Color.neutral60} />
                  </IconButton>
                </html.div>
              )
            }
          >
            <SkillAssigneeDropdown
              skill={skill}
              agencyUsers={agencyUsers}
              invalidate={invalidate}
            />
          </Dropdown>
        )}

        <Dropdown
          trigger={
            <html.div style={[Style.row, Style.alignCenter]}>
              {skill.status ? (
                <StatusTag status={skill.status} />
              ) : (
                <Text typeface="body.regular" color="neutral80">
                  Pending
                </Text>
              )}
              <IconButton aria-label={`Edit ${skill.type} status`}>
                <PencilSimpleIcon size={20} color={Color.neutral60} />
              </IconButton>
            </html.div>
          }
        >
          {SKILL_STATUSES.map(({ value }) => (
            <Dropdown.Row
              key={value}
              onClick={() => patchSkill({ status: value })}
            >
              <StatusTag status={value} />
              {skill.status === value && (
                <html.div
                  style={styles.cancelStatus}
                  onClick={(e) => {
                    e.stopPropagation();
                    patchSkill({ status: null });
                  }}
                >
                  <XIcon size={16} color={Color.neutral60} />
                </html.div>
              )}
            </Dropdown.Row>
          ))}
        </Dropdown>
      </html.div>
    </Stack>
  );
}

const styles = css.create({
  card: {
    backgroundColor: color.neutral10,
    borderRadius: 12,
    padding: 16,
  },
  divider: {
    width: '100%',
    height: 0,
    borderWidth: 0,
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    borderTopColor: color.neutral20,
    margin: 0,
  },
  pill: {
    backgroundColor: color.neutral20,
    borderRadius: 32,
    paddingInline: 8,
    display: 'flex',
    alignItems: 'center',
  },
  emptyPill: {
    justifyContent: 'center',
    width: 40,
  },
  skillContent: {
    gap: 16,
  },
  tag: {
    paddingInline: 6,
    paddingBlock: 2,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'solid',
    overflow: 'hidden',
  },
  tagColors: (bg: string, border: string) => ({
    backgroundColor: bg,
    borderColor: border,
  }),
  cancelStatus: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
});
